(() => {
  'use strict';

  /* ======================================================================
   * Config / Utils
   * ====================================================================== */
  const DEBUG          = true;
  const LOCALE         = 'es-AR';
  const VAT_DEF        = 1.21;
  const REMOVE_ANIM_MS = 500;
  const I18N            = window.AR33_i18n || {};

  const productCache               = new Map();
  const variantCache               = new Map();
  const productPromiseByContainer  = new WeakMap();
  const variantReqTokenByContainer = new WeakMap();

  const CURRENCY_FMT = new Intl.NumberFormat(LOCALE, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const qs    = (root, sel) => (root ? root.querySelector(sel)                : null);
  const qsa   = (root, sel) => (root ? Array.from(root.querySelectorAll(sel)) : []);
  const log   = (...a)      => { if (DEBUG) console.log(...a); };
  const money = (cents)     => '$' + CURRENCY_FMT.format(Number(cents || 0) / 100);
  const raf   = (cb)        => (typeof requestAnimationFrame === 'function' ? requestAnimationFrame(cb) : setTimeout(cb, 16));

  const matches = (el, sel) => {
    if (!el || typeof sel !== 'string') return false;
    const fn = el.matches || el.msMatchesSelector || el.webkitMatchesSelector;
    return fn ? fn.call(el, sel) : false;
  };

  const fetchJSON = async (url, signal) => {
    const opts = { headers: { Accept: 'application/json' } };
    if (signal) opts.signal = signal;
    const r = await fetch(url, opts);
    if (!r.ok) throw new Error('HTTP ' + r.status + ' @ ' + url);
    return r.json();
  };

  const openCartAside = () => {
    if (W.AR33?.openAside) {
      W.AR33.openAside('Cart');
    } else {
      setTimeout(() => W.AR33?.openAside?.('Cart'), 100);
    }
  };

  const W = window;

  /* ======================================================================
   * Taxes helpers
   * ====================================================================== */
  const elementHasDataTaxes = (el) =>
    !!(el && (
      (el.dataset && Object.prototype.hasOwnProperty.call(el.dataset, 'taxes')) ||
      (typeof el.hasAttribute === 'function' && el.hasAttribute('data-taxes'))
    ));

  const hasTaxesFlag = (container) => {
    if (!container) return false;
    if (elementHasDataTaxes(container)) return true;
    try { if (container.querySelector('[data-taxes]')) return true; } catch (_e) {}
    const priceEl = qs(container, 'price');
    if (elementHasDataTaxes(priceEl)) return true;
    if (priceEl?.closest) {
      const ancestor = priceEl.closest('[data-taxes]');
      if (ancestor && container.contains(ancestor)) return true;
    }
    return false;
  };

  const vatOf = (container) => {
    if (container?.dataset?.vat) {
      const v = Number(container.dataset.vat.trim().replace(',', '.'));
      if (Number.isFinite(v) && v > 0) return v;
    }
    return VAT_DEF;
  };

  /* ======================================================================
   * Producto / Variante
   * ====================================================================== */
  const getHandleFromContainer = (container) => {
    let handle = container?.dataset?.productHandle || container?.dataset?.handle || null;
    if (!handle) {
      const link = qs(container, 'a[href*="/products/"]');
      if (!link) return null;
      try {
        const parts = new URL(link.href, location.origin).pathname.split('/');
        const i     = parts.indexOf('products');
        handle      = i >= 0 ? (parts[i + 1] || '').split('?')[0] : null;
      } catch (_e) { handle = null; }
    }
    return handle;
  };

  const safeParseJSON = (txt) => { try { return JSON.parse(String(txt || '').trim()); } catch { return null; } };

  const getProductFromContainer = async (container, abortSignal) => {
    if (!container) return null;
    if (productPromiseByContainer.has(container)) return productPromiseByContainer.get(container);

    const p = (async () => {
      const script = qs(container, 'script[type="application/json"][data-product-json]');
      if (script?.textContent) { const j = safeParseJSON(script.textContent); if (j?.variants) return j; }
      if (container.dataset?.product) { const j = safeParseJSON(container.dataset.product); if (j?.variants) return j; }
      if (W.getJSON?.variants) return W.getJSON;
      const handle = getHandleFromContainer(container);
      if (!handle) return null;
      if (productCache.has(handle)) return productCache.get(handle);
      const fetched = await fetchJSON('/products/' + encodeURIComponent(handle) + '.js', abortSignal);
      productCache.set(handle, fetched);
      return fetched;
    })();

    productPromiseByContainer.set(container, p);
    return p;
  };

  const getVariantById = async (id, abortSignal) => {
    const key = String(id);
    if (variantCache.has(key)) return variantCache.get(key);
    const v = await fetchJSON('/variants/' + encodeURIComponent(id) + '.js', abortSignal);
    variantCache.set(key, v);
    return v;
  };

  const pickDefaultVariant = (product) => {
    if (!product?.variants?.length) return null;
    return product.variants.find(v => v?.available !== false) || product.variants[0] || null;
  };

  /* ======================================================================
   * Scope helpers
   * ====================================================================== */
  const getContainer = (el) => {
    if (!el?.closest) return null;
    return el.closest('[data-product-scope]') ||
           el.closest('card')                 ||
           el.closest('[data-product-json]')  ||
           el.closest('form[action*="/cart/add"]') ||
           null;
  };

  const isCardScope = (node) => !!(node?.closest?.('card'));

  const hasUpdateUrlFlag = (container) =>
    !!(container?.dataset && String(container.dataset.updateUrl).toLowerCase() === 'true');

  /* ======================================================================
   * Render: Precio / S.I.N. / Cuotas / Links / URL
   * ====================================================================== */
  const ensureSinSpan = (host) => {
    let span = host.querySelector('span[data-sin]');
    if (!span) { span = document.createElement('span'); span.setAttribute('data-sin', ''); host.appendChild(span); }
    return span;
  };

  const removeSinSpan = (host) => { host.querySelector('span[data-sin]')?.remove(); };
  const clearNode     = (el)   => { while (el?.firstChild) el.removeChild(el.firstChild); };

  /* ------------------------------------------------------------
   * renderPriceIn
   * Escenario 1 (price=0): "Precio no disponible" + "S.I.N.: N/D"
   * Escenario 2/3: precio normal
   * ------------------------------------------------------------ */
  const renderPriceIn = (container, variant) => {
    const host = qs(container, 'price');
    if (!host) return;

    const priceCents   = Number(variant.price || 0);
    const compareCents = Number(variant.compare_at_price || 0);

    clearNode(host);

    if (priceCents === 0) {
      host.appendChild(document.createTextNode(I18N.priceUnavailable || 'Precio no disponible'));
      const sinSpan       = ensureSinSpan(host);
      sinSpan.style.display = '';
      sinSpan.textContent   = ' ' + (I18N.taxPriceLabel || 'Precio S.I.N.:') + ' ' + (I18N.na || 'N/D');
      return;
    }

    if (compareCents > priceCents) {
      const del = document.createElement('del');
      del.textContent = money(compareCents);
      host.appendChild(del);
      host.appendChild(document.createTextNode(' '));
    }
    host.appendChild(document.createTextNode(money(priceCents)));

    if (hasTaxesFlag(container)) {
      const sinSpan       = ensureSinSpan(host);
      sinSpan.style.display = '';
      sinSpan.textContent   = ' ' + (I18N.taxPriceLabel || 'Precio S.I.N.:') + ' ' + money(Math.round(priceCents / vatOf(container)));
    } else {
      removeSinSpan(host);
    }
  };

  /* ------------------------------------------------------------
   * renderInstallmentsIn
   * Escenario 1 (price=0): "N/D"
   * Escenario 2/3: cuotas normal
   * ------------------------------------------------------------ */
  const renderInstallmentsIn = (container, variant) => {
    const host = qs(container, 'quota');
    if (!host) return;

    if (Number(variant.price || 0) === 0) {
      host.textContent = (I18N.na || 'N/D');
      return;
    }

    const q = parseInt(host.getAttribute('data-installments'), 10);
    host.textContent = (Number.isFinite(q) && q > 1)
      ? (q + ' ' + (I18N.installmentsOf || 'Cuotas de') + ' ' + money(Math.round((variant.price || 0) / q)))
      : '';
  };

  const updateLinksVariantIn = (container, variantId) => {
    if (!isCardScope(container)) return;
    const handle = getHandleFromContainer(container);
    if (!handle) return;
    for (const a of qsa(container, 'a[href*="/products/"]')) {
      const raw = a.getAttribute('href') || '';
      if (!raw || raw.startsWith('#') || raw.startsWith('javascript:')) continue;
      // No tocar el link de "Ver Producto" — solo links de navegación
      if (a.hasAttribute('data-ver-producto')) continue;
      try {
        const u     = new URL(a.href, location.origin);
        const parts = u.pathname.split('/');
        const ix    = parts.indexOf('products');
        if (ix >= 0 && (parts[ix + 1] || '').split('?')[0] === handle) {
          u.searchParams.set('variant', String(variantId));
          a.href = u.toString();
        }
      } catch (_e) {}
    }
  };

  const maybeUpdateURL = (container, variantId) => {
    if (isCardScope(container) || !hasUpdateUrlFlag(container)) return;
    const u = new URL(location.href);
    u.searchParams.set('variant', variantId);
    history.replaceState({}, '', u);
  };

  const setHiddenVariantId = (container, variantId) => {
    const id = qs(container, 'input[name="id"]');
    if (id) id.value = variantId;
  };

  /* ======================================================================
   * Selección de variante desde el formulario
   * ====================================================================== */
  const getVariantIdFromForm = (container) => {
    const form = qs(container, 'form[action*="/cart/add"]');
    if (!form) return null;
    const r = form.querySelector('input[type="radio"][name="id"]:checked');
    if (r?.value && /^\d+$/.test(r.value)) return Number(r.value);
    const sel = form.querySelector('select[name="id"]');
    if (sel?.value && /^\d+$/.test(sel.value)) return Number(sel.value);
    const hid = form.querySelector('input[name="id"]');
    if (hid?.value && /^\d+$/.test(hid.value)) return Number(hid.value);
    return null;
  };

  /* ======================================================================
   * CTA helpers
   * ====================================================================== */
  const isCatalogContainer = (container) => !!(container?.dataset?.catalog === 'true');

  const getPhoneFromContainer = (container) => {
    if (container?.dataset?.target?.trim()) return container.dataset.target.trim();
    const el = container?.querySelector('[data-target]');
    return el?.dataset?.target?.trim() || '';
  };

  const buildVariantInfo = (product, variant) => ({
    nombre: String(product?.title || ''),
    talle:  String(variant?.option1 || ''),
    color:  String(variant?.option2 || '')
  });

  const insertAfter = (ref, newNode) => {
    if (ref?.parentNode && newNode) ref.parentNode.insertBefore(newNode, ref.nextSibling);
  };

  // Devuelve el <item> que contiene los elementos CTA del formulario
  const getCtaScope = (container) => {
    const btn = qs(container, 'form[action*="/cart/add"] button[type="submit"]');
    if (btn) return btn.closest('item') || btn.parentElement;
    // Card con precio 0 inicial: no hay button, buscar por el link "Ver Producto"
    const verLink = qs(container, 'a[data-ver-producto]');
    if (verLink) return verLink.closest('item') || verLink.parentElement;
    // Fallback: buscar por WSP
    const wsp = qs(container, 'form[action*="/cart/add"] #isWSP');
    if (wsp) return wsp.closest('item') || wsp.parentElement;
    return null;
  };

  // Obtiene la URL del producto desde el handle o un link existente
  const getProductUrlFromContainer = (container) => {
    const handle = getHandleFromContainer(container);
    if (handle) return '/products/' + handle;
    try {
      const link = qs(container, 'a[href*="/products/"]');
      if (link) {
        const u = new URL(link.href, location.origin);
        u.searchParams.delete('variant');
        return u.pathname;
      }
    } catch (_e) {}
    return '#';
  };

  // Obtiene o crea el link "Ver Producto" (data-ver-producto) dentro de un scope
  const ensureVerProductoLink = (scope, href) => {
    if (!scope) return null;
    let a = scope.querySelector('a[data-ver-producto]');
    if (!a) {
      a = document.createElement('a');
      a.setAttribute('data-ver-producto', '');
      a.className   = 'setButton';
      a.textContent = I18N.viewProduct || 'Ver Producto';
      scope.appendChild(a);
    }
    a.href          = href;
    a.style.display = '';
    return a;
  };

  const hideVerProductoLink = (scope) => {
    if (!scope) return;
    const a = scope.querySelector('a[data-ver-producto]');
    if (a) a.style.display = 'none';
  };

  // Obtiene o crea #isWSP dentro de un scope (para cards)
  const ensureWspInScope = (scope, href) => {
    if (!scope) return null;
    let a = scope.querySelector('#isWSP');
    if (!a) {
      a             = document.createElement('a');
      a.id          = 'isWSP';
      a.textContent = I18N.inquire || 'Consultar';
      a.target      = '_blank';
      a.rel         = 'noopener';
      scope.appendChild(a);
    }
    a.href          = href;
    a.style.display = '';
    return a;
  };

  // Crea o recupera #isWSP y opcionalmente #isMSG anclados al submitBtn (páginas de producto)
  const ensureNodesAfterSubmit = (submitBtn, withMsg) => {
    if (!submitBtn) return { a: null, p: null };
    const scope = submitBtn.closest('item') || submitBtn.parentElement;

    let a = scope?.querySelector('#isWSP');
    if (!a) {
      a             = document.createElement('a');
      a.id          = 'isWSP';
      a.textContent = I18N.inquire || 'Consultar';
      a.target      = '_blank';
      a.rel         = 'noopener';
      insertAfter(submitBtn, a);
    } else {
      insertAfter(submitBtn, a);
    }

    let p = null;
    if (withMsg) {
      p = scope?.querySelector('#isMSG');
      if (!p) {
        p    = document.createElement('p');
        p.id = 'isMSG';
        insertAfter(a, p);
      } else {
        insertAfter(a, p);
      }
    }
    return { a, p };
  };

  // Crea o recupera solo #isMSG anclado al submitBtn (página producto, precio 0)
  const ensureMsgAfterSubmit = (submitBtn) => {
    if (!submitBtn) return null;
    const scope = submitBtn.closest('item') || submitBtn.parentElement;
    let p = scope?.querySelector('#isMSG');
    if (!p) {
      p    = document.createElement('p');
      p.id = 'isMSG';
      insertAfter(submitBtn, p);
    }
    return p;
  };

  const removeNodesNearSubmit = (submitBtn) => {
    if (!submitBtn) return;
    const scope = submitBtn.closest('item') || submitBtn.parentElement || submitBtn.ownerDocument;
    scope.querySelector('#isWSP')?.remove();
    scope.querySelector('#isMSG')?.remove();
  };

  /* ======================================================================
   * Render de estado de variante
   *
   * ── CARD ──────────────────────────────────────────────────────────────
   *  Escenario 1 │ price=0     │ "Ver Producto" (link)  │ sin MSG
   *  Escenario 2 │ sin stock   │ botón oculto + WSP     │ sin MSG
   *  Escenario 3 │ disponible  │ "Agregar" (button)     │ sin MSG
   *
   * ── PÁGINA DE PRODUCTO ────────────────────────────────────────────────
   *  Escenario 1 │ price=0     │ "Consultar" disabled   │ MSG producto
   *  Escenario 2 │ sin stock   │ botón oculto + WSP     │ MSG talle
   *  Escenario 3 │ disponible  │ "Agregar" (button)     │ sin MSG
   * ====================================================================== */
  const renderVariantState = (container, variant, product) => {
    raf(() => {
      setHiddenVariantId(container, variant.id);
      updateLinksVariantIn(container, variant.id);
      renderPriceIn(container, variant);
      renderInstallmentsIn(container, variant);
      maybeUpdateURL(container, variant.id);

      const inCard     = isCardScope(container);
      const priceCents = Number(variant.price || 0);
      const isAvail    = variant.available !== false;

      const submitBtn  = qs(container, 'form[action*="/cart/add"] button[type="submit"]');
      const ctaScope   = getCtaScope(container);

      if (!ctaScope) return;

      if (submitBtn && !submitBtn.dataset.origText) {
        submitBtn.dataset.origText = (submitBtn.textContent || '').trim() || (isCatalogContainer(container) ? (I18N.addToOrder || 'Agregar al Pedido') : (I18N.addToCart || 'Agregar'));
      }

      const tel = getPhoneFromContainer(container) || '';
      const { nombre, talle, color } = buildVariantInfo(product, variant);
      const wspMessage = (I18N.whatsappTemplate || 'Estoy interesada en __PRODUCT__ en talle __SIZE__ en color __COLOR__')
        .replace('__PRODUCT__', nombre).replace('__SIZE__', talle).replace('__COLOR__', color);
      const wspHref    = `https://wa.me/${tel}?text=${encodeURIComponent(wspMessage)}`;
      const productUrl = getProductUrlFromContainer(container);

      const isCatalog = isCatalogContainer(container);

      if (inCard) {
        /* ── CARD ── */
        if (isCatalog) {
          // Catálogo B2B: sin precio, sin WhatsApp, botón deshabilitado con "Próximamente" si no hay stock
          hideVerProductoLink(ctaScope);
          ctaScope.querySelector('#isWSP')?.remove();
          ctaScope.querySelector('#isMSG')?.remove();
          if (submitBtn) {
            if (isAvail) {
              submitBtn.style.display = '';
              submitBtn.disabled      = false;
              submitBtn.textContent   = submitBtn.dataset.origText || (I18N.addToCart || 'Agregar');
            } else {
              submitBtn.style.display = '';
              submitBtn.disabled      = true;
              submitBtn.textContent   = I18N.comingSoon || 'Próximamente';
            }
          }

        } else if (priceCents === 0) {
          // Escenario 1 card: link "Ver Producto"
          if (submitBtn) { submitBtn.style.display = 'none'; submitBtn.disabled = true; }
          ensureVerProductoLink(ctaScope, productUrl);
          ctaScope.querySelector('#isWSP')?.remove();
          ctaScope.querySelector('#isMSG')?.remove();

        } else if (isAvail) {
          // Escenario 3 card: botón "Agregar"
          hideVerProductoLink(ctaScope);
          if (submitBtn) {
            submitBtn.style.display = '';
            submitBtn.disabled      = false;
            submitBtn.textContent   = submitBtn.dataset.origText || (I18N.addToCart || 'Agregar');
          }
          ctaScope.querySelector('#isWSP')?.remove();
          ctaScope.querySelector('#isMSG')?.remove();

        } else {
          // Escenario 2 card: botón oculto + WSP, sin mensaje
          hideVerProductoLink(ctaScope);
          if (submitBtn) { submitBtn.style.display = 'none'; submitBtn.disabled = true; }
          ensureWspInScope(ctaScope, wspHref);
          ctaScope.querySelector('#isMSG')?.remove();
        }

      } else {
        /* ── PÁGINA DE PRODUCTO ── */
        if (!submitBtn) return;

        if (isCatalog) {
          // Catálogo B2B: sin precio, sin WhatsApp, botón deshabilitado con "Próximamente" si no hay stock
          removeNodesNearSubmit(submitBtn);
          if (isAvail) {
            submitBtn.style.display = '';
            submitBtn.disabled      = false;
            submitBtn.textContent   = submitBtn.dataset.origText || (I18N.addToCart || 'Agregar');
          } else {
            submitBtn.style.display = '';
            submitBtn.disabled      = true;
            submitBtn.textContent   = I18N.comingSoon || 'Próximamente';
          }

        } else if (priceCents === 0) {
          // Escenario 1 página: "Consultar" disabled + mensaje producto
          submitBtn.style.display = '';
          submitBtn.disabled      = true;
          submitBtn.textContent   = I18N.inquire || 'Consultar';
          ctaScope.querySelector('#isWSP')?.remove();
          const p = ensureMsgAfterSubmit(submitBtn);
          if (p) {
            p.textContent   = I18N.unavailableProduct || 'Este producto actualmente no se encuentra disponible. Podés comunicarte con nuestro equipo para consultar si es posible de realizarlo exclusivamente para vos.';
            p.style.display = '';
          }

        } else if (isAvail) {
          // Escenario 3 página: "Agregar"
          submitBtn.style.display = '';
          submitBtn.disabled      = false;
          submitBtn.textContent   = submitBtn.dataset.origText || (I18N.addToCart || 'Agregar');
          removeNodesNearSubmit(submitBtn);

        } else {
          // Escenario 2 página: botón oculto + WSP + mensaje talle
          submitBtn.style.display = 'none';
          submitBtn.disabled      = true;
          const { a, p } = ensureNodesAfterSubmit(submitBtn, true);
          if (a) { a.href = wspHref; a.style.display = ''; }
          if (p) {
            p.textContent   = I18N.unavailableVariant || 'Este talle actualmente no se encuentra disponible. Podés comunicarte con nuestro equipo para consultar si es posible de realizarlo exclusivamente para vos.';
            p.style.display = '';
          }
        }
      }

      log('[VARIANT]', {
        id: variant.id, price: priceCents, available: variant.available, inCard,
        taxes: hasTaxesFlag(container)
      });
    });
  };

  /* ======================================================================
   * Resolución de variante
   * ====================================================================== */
  const resolveVariant = async (container, directVariantId) => {
    const next = (variantReqTokenByContainer.get(container) || 0) + 1;
    variantReqTokenByContainer.set(container, next);
    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;

    try {
      let variantId = Number(directVariantId);
      if (!Number.isFinite(variantId)) variantId = getVariantIdFromForm(container);
      if (!Number.isFinite(variantId)) return null;

      const product = await getProductFromContainer(container, ctrl?.signal);
      let variant   = product?.variants?.find(v => Number(v.id) === variantId) || null;
      if (!variant)  variant = await getVariantById(variantId, ctrl?.signal);
      if (!variant)  return null;

      if (variantReqTokenByContainer.get(container) !== next) return null;
      return variant;
    } catch (err) {
      if (DEBUG) console.log('resolveVariant error:', err);
      return null;
    }
  };

  const resolveAndRenderVariant = async (originEl, opts = {}) => {
    const container = getContainer(originEl) || originEl;
    if (!container) return;
    const variant = await resolveVariant(container, opts.directVariantId);
    if (!variant) return;
    let product = null;
    try { product = await getProductFromContainer(container); } catch (_e) {}
    renderVariantState(container, variant, product);
  };

  /* ======================================================================
   * Init precio si <price> está vacío al cargar
   * ====================================================================== */
  const ensureInitialPriceIfEmpty = async (container) => {
    const priceEl = qs(container, 'price');
    if (!priceEl || (priceEl.innerHTML || '').trim()) return;

    try {
      const product       = await getProductFromContainer(container);
      const formVariantId = getVariantIdFromForm(container);
      let variant         = null;

      if (Number.isFinite(formVariantId)) {
        variant = product?.variants?.find(v => Number(v.id) === formVariantId) || null;
        if (!variant) variant = await getVariantById(formVariantId);
      } else {
        variant = pickDefaultVariant(product);
      }

      if (!variant) return;
      renderVariantState(container, variant, product);
      log('[INIT] price filled', { id: variant.id, inCard: isCardScope(container) });
    } catch (err) {
      console.log('[INIT] error:', err);
    }
  };

  /* ======================================================================
   * Cart UI helpers
   * ====================================================================== */
  const updateCartCounters = (cart) => {
    const cnt = document.querySelector('[data-cart-count]');
    const ttl = document.querySelector('[data-cart-total]');
    if (cnt) cnt.textContent = String(cart?.item_count || 0);
    if (ttl) ttl.textContent = money(cart?.total_price || 0);
  };

  const rememberDisplay = (el) => {
    if (!el || el.dataset.origDisplay) return;
    const cs      = getComputedStyle(el);
    const current = (el.style.display?.trim()) || cs.display || 'block';
    el.dataset.origDisplay = current === 'none' ? 'block' : current;
  };

  const isPedidoCart = (cart) =>
    Array.isArray(cart?.items) && cart.items.some(it => it?.properties && String(it.properties._catalog) === 'true');

  const applyCartModeState = (cart) => {
    const cartSection = document.querySelector('#Cart');
    if (!cartSection) return;

    const pedido    = isPedidoCart(cart);
    const itemCount = Number(cart?.item_count) || 0;

    const form = qs(cartSection, 'form');
    if (form) form.dataset.pedido = pedido ? 'true' : 'false';

    const subtotal = qs(cartSection, '#isSubtotal');
    const buy      = qs(cartSection, '#isActionsBuy');
    const ped      = qs(cartSection, '#isActionsPedido');
    const comment  = qs(cartSection, '#isComment');

    if (subtotal) subtotal.style.display = (itemCount > 0 && !pedido) ? 'flex' : 'none';
    if (buy)      buy.style.display      = (itemCount > 0 && !pedido) ? 'flex' : 'none';
    if (ped)      ped.style.display      = (itemCount > 0 && pedido)  ? 'flex' : 'none';
    if (comment)  comment.style.display  = (itemCount > 0 && pedido)  ? 'flex' : 'none';

    for (const p of qsa(cartSection, 'form price')) {
      p.style.display = pedido ? 'none' : '';
    }
  };

  const applyCartEmptyState = (cartOrCount) => {
    const cartSection = document.querySelector('#Cart');
    if (!cartSection) return;

    const isEmpty = (typeof cartOrCount === 'number' ? cartOrCount : Number(cartOrCount?.item_count) || 0) <= 0;

    const emptyItem = qs(cartSection, 'item.isEmpty');
    if (emptyItem) {
      rememberDisplay(emptyItem);
      emptyItem.style.display = isEmpty ? emptyItem.dataset.origDisplay : 'none';
    }

    for (const el of qsa(cartSection, '#Cart .isFixed')) {
      rememberDisplay(el);
      el.style.display = isEmpty ? 'none' : 'flex';
    }
  };

  /* ======================================================================
   * Cart: renderizar / actualizar línea de producto
   * ====================================================================== */
  const sanitize = (v) => (v == null ? '' : String(v));

  const renderOrUpdateItemInCartForm = (line, cart) => {
    const form = document.querySelector('#Cart > form');
    if (!form || !line) return;

    const pedido = isPedidoCart(cart);
    const idStr  = String(line.id);
    const itemEl = form.querySelector('item[data-id="' + idStr + '"]');

    let optionsHtml = '';
    if (Array.isArray(line.options_with_values) && line.options_with_values.length) {
      optionsHtml = line.options_with_values.map(ov => sanitize(ov.name) + ': ' + sanitize(ov.value)).join(' <span>|</span> ');
    } else if (line.variant_title?.trim()) {
      optionsHtml = sanitize(line.variant_title).split(' / ').join(' <span>|</span> ');
    }

    const smallHtml = 'Cantidad: ' + (line.quantity || 1) + (optionsHtml ? ' <span>|</span> ' + optionsHtml : '');
    const unit      = typeof line.final_price === 'number' ? line.final_price : (line.price || 0);
    const lineTotal = typeof line.final_line_price === 'number' ? line.final_line_price : unit * (line.quantity || 1);

    if (itemEl) {
      const sm = qs(itemEl, 'small'); if (sm) sm.innerHTML = smallHtml;
      const pe = qs(itemEl, 'price'); if (pe) pe.textContent = pedido ? '' : money(lineTotal);
      return;
    }

    const wrapper = document.createElement('item');
    wrapper.setAttribute('data-id', idStr);
    wrapper.innerHTML =
      '<card>'
      + '<a href="' + sanitize(line.url || '#') + '"><img src="' + sanitize(line.image || '') + '" alt="' + sanitize(line.title || '') + '"></a>'
      + '<div>'
      + '<p>' + sanitize(line.product_title || line.title || '') + '</p>'
      + (pedido ? '' : ('<price>' + money(lineTotal) + '</price>'))
      + '<small>' + smallHtml + '</small>'
      + '<p><a href="#" class="setLinks" data-key="' + sanitize(line.key || '') + '">' + (I18N.removeItem || 'Eliminar') + '</a></p>'
      + '</div></card>';
    form.insertBefore(wrapper, form.firstChild);
  };

  /* ======================================================================
   * Add to cart
   * ====================================================================== */
  let addingLock = false;

  const addToCart = async (form) => {
    if (addingLock) return;
    addingLock = true;

    const submitBtn = qs(form, 'button[type="submit"]');
    let origBtnText = '';
    if (submitBtn) {
      origBtnText = submitBtn.dataset.origText || (submitBtn.textContent || '').trim() || (I18N.addToCart || 'Agregar');
      submitBtn.dataset.origText = origBtnText;
      submitBtn.textContent = I18N.addingToCart || 'Agregando...';
      submitBtn.disabled    = true;
      submitBtn.setAttribute('aria-busy', 'true');
    }

    try {
      const fd       = new FormData(form);
      const id       = Number(fd.get('id'));
      if (!Number.isFinite(id)) throw new Error('ID de variante inválido.');
      const quantity = parseInt(fd.get('quantity') || '1', 10) || 1;

      const props = {};
      fd.forEach((v, k) => { const m = String(k).match(/^properties\[(.+)\]$/); if (m) props[m[1]] = v; });

      const payload = { id, quantity };
      if (Object.keys(props).length) payload.properties = props;

      const addRes = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body:    JSON.stringify(payload)
      });
      if (!addRes.ok) throw new Error('No se pudo agregar el producto.');

      const addedLine = await addRes.json();
      const cart      = await fetchJSON('/cart.js');

      updateCartCounters(cart);
      renderOrUpdateItemInCartForm(addedLine, cart);
      applyCartEmptyState(cart);
      applyCartModeState(cart);
      openCartAside();

    } catch (err) {
      console.log('Error al agregar:', err);
    } finally {
      if (submitBtn) {
        submitBtn.textContent = origBtnText || (I18N.addToCart || 'Agregar');
        submitBtn.disabled    = false;
        submitBtn.removeAttribute('aria-busy');
      }
      addingLock = false;
    }
  };

  /* ======================================================================
   * Pedido B2B: envío de la lista de productos por email (form 'contact')
   * ====================================================================== */
  const showOrderMessage = (text, isError) => {
    const el = document.querySelector('#isOrderMsg');
    if (!el) return;
    el.textContent   = text;
    el.style.display = '';
    el.classList.toggle('isError', !!isError);
    setTimeout(() => { el.style.display = 'none'; }, 6000);
  };

  const buildOrderId = () => {
    const season = (I18N.season || '').trim();
    const now    = new Date();
    const pad    = (n) => String(n).padStart(2, '0');
    const stamp  = String(now.getFullYear()).slice(-2) + pad(now.getMonth() + 1) + pad(now.getDate())
      + '-' + pad(now.getHours()) + pad(now.getMinutes());
    return (season ? season + '-' : '') + stamp;
  };

  const buildOrderText = (cart) => (cart.items || []).map((it) => {
    let variantHtml = '';
    if (Array.isArray(it.options_with_values) && it.options_with_values.length) {
      variantHtml = it.options_with_values
        .filter(ov => sanitize(ov.value) && sanitize(ov.value) !== 'Default Title')
        .map(ov => sanitize(ov.name) + ': ' + sanitize(ov.value)).join(', ');
    } else if (it.variant_title && it.variant_title !== 'Default Title') {
      variantHtml = sanitize(it.variant_title);
    }
    return '- ' + sanitize(it.product_title || it.title)
      + ' | SKU: ' + sanitize(it.sku)
      + ' | Cantidad: ' + (it.quantity || 1)
      + (variantHtml ? ' | Variante: ' + variantHtml : '');
  }).join('\n');

  let orderSending = false;

  const submitOrder = async () => {
    if (orderSending) return;
    orderSending = true;

    const btn      = document.querySelector('#isPedido');
    const origText = btn ? btn.textContent : '';

    if (btn) {
      btn.textContent = I18N.sendingOrder || 'Enviando...';
      btn.disabled    = true;
    }

    try {
      const cart = await fetchJSON('/cart.js');
      if (!cart.items || !cart.items.length) throw new Error('El pedido está vacío.');

      const orderForm = document.querySelector('#CartOrder');
      if (!orderForm) throw new Error('No se encontró el formulario de pedido.');

      const orderId      = buildOrderId();
      const commentField = document.querySelector('#isCartComment');
      const comment      = commentField ? commentField.value.trim() : '';

      const idField      = orderForm.querySelector('#isOrderId');
      const commentHidden = orderForm.querySelector('#isOrderComment');
      const subjectField = orderForm.querySelector('input[name="contact[Asunto]"]');
      if (idField) idField.value = orderId;
      if (commentHidden) commentHidden.value = comment;
      if (subjectField) subjectField.value = 'Orden de Compra - ' + orderId;

      const bodyField = orderForm.querySelector('#isOrderBody');
      if (bodyField) {
        bodyField.value = 'Pedido: ' + orderId
          + (comment ? '\nComentario general: ' + comment : '')
          + '\n\n' + buildOrderText(cart);
      }

      const res = await fetch(orderForm.action, {
        method:  'POST',
        body:    new FormData(orderForm),
        headers: { 'Accept': 'text/html' }
      });
      if (!res.ok) throw new Error('HTTP ' + res.status + ' al enviar el pedido.');

      await fetch('/cart/clear.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
      });

      const clearedCart = await fetchJSON('/cart.js');
      const cartForm     = document.querySelector('#Cart > form');
      if (cartForm) qsa(cartForm, 'item[data-id]').forEach(el => el.remove());
      if (commentField) commentField.value = '';

      updateCartCounters(clearedCart);
      applyCartEmptyState(clearedCart);
      applyCartModeState(clearedCart);
      showOrderMessage(I18N.orderSent || 'Tu pedido fue enviado con éxito.', false);

    } catch (err) {
      console.log('Error al enviar el pedido:', err);
      showOrderMessage(I18N.orderError || 'No pudimos enviar tu pedido. Intentá nuevamente.', true);
    } finally {
      if (btn) {
        btn.textContent = origText || (I18N.sendOrder || 'Enviar Pedido');
        btn.disabled    = false;
      }
      orderSending = false;
    }
  };

  /* ======================================================================
   * Listeners
   * ====================================================================== */
  document.addEventListener('click', (e) => {
    const btn = e.target?.closest?.('#isPedido');
    if (!btn) return;
    e.preventDefault();
    submitOrder();
  });

  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!/\/cart\/add/i.test(form.getAttribute('action') || '')) return;
    e.preventDefault();
    addToCart(form);
  });

  document.addEventListener('change', (e) => {
    const t = e.target;
    if (!t || !(t instanceof HTMLElement)) return;
    if (matches(t, 'input[type="radio"]') && /^\d+$/.test(String(t.value))) {
      resolveAndRenderVariant(t, { directVariantId: Number(t.value) });
      return;
    }
    if (matches(t, 'select[name="id"], input[name="id"]') && /^\d+$/.test(String(t.value))) {
      resolveAndRenderVariant(t, { directVariantId: Number(t.value) });
    }
  });

  document.addEventListener('click', async (e) => {
    const link = e.target?.closest?.('#Cart .setLinks');
    if (!link) return;
    e.preventDefault();

    const it = link.closest('item');
    if (it) it.classList.add('isRemove');

    try {
      const key = link.getAttribute('data-key');
      if (!key) return;

      const res = await fetch('/cart/change.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body:    JSON.stringify({ id: key, quantity: 0 })
      });
      if (!res.ok) throw new Error('No se pudo eliminar el ítem.');

      const cart = await res.json();

      if (it) setTimeout(() => { try { it.remove(); } catch (_e) {} }, REMOVE_ANIM_MS);

      updateCartCounters(cart);

      const applyEmpty = () => { applyCartEmptyState(cart); applyCartModeState(cart); };
      if (Number(cart?.item_count) === 0) {
        setTimeout(applyEmpty, REMOVE_ANIM_MS);
      } else {
        applyEmpty();
      }

    } catch (err) {
      console.log('Error al eliminar:', err);
    }
  });

  /* ======================================================================
   * Init
   * ====================================================================== */
  document.addEventListener('DOMContentLoaded', async () => {
    const scopes     = qsa(document, '[data-product-scope], card, form[action*="/cart/add"]');
    const urlVariant = Number(new URL(location.href).searchParams.get('variant'));

    for (let i = 0; i < scopes.length; i++) {
      const container = scopes[i];
      await ensureInitialPriceIfEmpty(container); // eslint-disable-line no-await-in-loop

      if (Number.isFinite(urlVariant)) {
        try {
          const product = await getProductFromContainer(container); // eslint-disable-line no-await-in-loop
          if (product?.variants?.some(v => Number(v.id) === urlVariant)) {
            await resolveAndRenderVariant(container, { directVariantId: urlVariant }); // eslint-disable-line no-await-in-loop
          }
        } catch (_e) {}
      }
    }

    try {
      const cart = await fetchJSON('/cart.js');
      updateCartCounters(cart);
      applyCartModeState(cart);
    } catch (e) {
      if (DEBUG) console.log('Cart inicial no disponible:', e);
    }
  });

})();